// @ts-nocheck
import { useQueryClient, useMutation } from 'react-query';
import { useRequestQuery } from '../useQueryRequest';
import { transformPagination } from '@/utils';
import useApiRequest from '../useRequest';
import { useRequestPdf } from '../useRequestPdf';
import t from './types';

// Common invalidate queries.
const commonInvalidateQueries = (queryClient) => {
  // Invalidate invoices.
  queryClient.invalidateQueries(t.SALE_INVOICES);
  queryClient.invalidateQueries(t.SALE_INVOICE);

  // Invalidate customers.
  queryClient.invalidateQueries(t.CUSTOMERS);

  // Invalidate accounts.
  queryClient.invalidateQueries(t.ITEMS);
  queryClient.invalidateQueries(t.ITEM);

  // Invalidate settings.
  queryClient.invalidateQueries([t.SETTING, t.SETTING_INVOICES]);

  // Invalidate financial reports.
  queryClient.invalidateQueries(t.FINANCIAL_REPORT);

  // Invalidate transactions by reference.
  queryClient.invalidateQueries(t.TRANSACTIONS_BY_REFERENCE);

  // Invalidate accounts.
  queryClient.invalidateQueries(t.ACCOUNTS);
  queryClient.invalidateQueries(t.ACCOUNT);

  // Invalidate reconcile.
  queryClient.invalidateQueries(t.RECONCILE_CREDIT_NOTE);
  queryClient.invalidateQueries(t.RECONCILE_CREDIT_NOTES);

  // Invalidate
  queryClient.invalidateQueries(t.ITEM_ASSOCIATED_WITH_INVOICES);

  // Invalidate item warehouses.
  queryClient.invalidateQueries(t.ITEM_WAREHOUSES_LOCATION);

  // Invalidate mutate base currency abilities.
  queryClient.invalidateQueries(t.ORGANIZATION_MUTATE_BASE_CURRENCY_ABILITIES);
};

/**
 * Generate E-Invoice.
 */

/**
 * Creates a new sale invoice.
 */
export function useCreateInvoice(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values) => apiRequest.post('sales/invoices', values), {
    onSuccess: (res, values) => {
      // Invalidate invoice customer.
      queryClient.invalidateQueries([t.CUSTOMER, values.customer_id]);

      // Invalidate estimates.
      queryClient.invalidateQueries(t.SALE_ESTIMATES);
      queryClient.invalidateQueries(t.SALE_ESTIMATE);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Edits the given sale invoice.
 */
export function useEditInvoice(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/invoices/${id}`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Invalidate specific sale invoice.
        queryClient.invalidateQueries([t.SALE_INVOICE, id]);

        // Invalidate invoice customer.
        queryClient.invalidateQueries([t.CUSTOMER, values.customer_id]);

        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Retrieve the commission details for the given invoice.
 */
export function useInvoiceCommission(invoiceId, props?, requestProps?) {
  return useRequestQuery(
    [t.SALE_INVOICE_COMMISSION, invoiceId],
    { method: 'get', url: `sales/invoices/${invoiceId}/commission`, ...requestProps },
    {
      select: (res) => res.data,
      defaultData: {},
      enabled: !!invoiceId,
      ...props,
    },
  );
}

/**
 * Edits/updates the commission for the given invoice.
 */
export function useEditInvoiceCommission(props?) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([invoiceId, values]) =>
      apiRequest.post(`sales/invoices/${invoiceId}/commission`, values),
    {
      onSuccess: (res, [invoiceId]) => {
        queryClient.invalidateQueries([t.SALE_INVOICE_COMMISSION, invoiceId]);
        queryClient.invalidateQueries([t.SALE_INVOICE, invoiceId]);
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}
/**
 * Creates or updates the commission snapshot for the given invoice.
 */
export function useUpsertInvoiceCommission(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    (invoiceId) => apiRequest.post(`sales/invoices/${invoiceId}/commission`),
    {
      onSuccess: (res, invoiceId) => {
        queryClient.invalidateQueries([t.SALE_INVOICE, invoiceId]);
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Deletes the given sale invoice.
 */
export function useDeleteInvoice(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.delete(`sales/invoices/${id}`), {
    onSuccess: (res, id) => {
      // Invalidate specific invoice.
      queryClient.invalidateQueries([t.SALE_INVOICE, id]);

      // Invalidate estimates.
      queryClient.invalidateQueries(t.SALE_ESTIMATES);
      queryClient.invalidateQueries(t.SALE_ESTIMATE);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

const transformInvoices = (res) => ({
  invoices: res.data.sales_invoices,
  pagination: transformPagination(res.data.pagination),
  filterMeta: res.data.filter_meta,
});

/**
 * Retrieve sale invoices list with pagination meta.
 */
export function useInvoices(query, props) {
  return useRequestQuery(
    [t.SALE_INVOICES, query],
    { method: 'get', url: 'sales/invoices', params: query },
    {
      select: transformInvoices,
      defaultData: {
        invoices: [],
        pagination: {
          page: 1,
          pageSize: 20,
          total: 0,
        },
        filterMeta: {},
      },
      ...props,
    },
  );
}

/**
 * Retrieve the E-Invoice details.
 * @param {number} invoiceId - Invoice id.
 */
export function useFilteredInvoices(props, requestProps) {
  return useRequestQuery(
    [t.FILTERED_INVOICES],
    { method: 'get', url: `sales/invoices/dashboard/saleInvoicesChart`, ...requestProps },
    {
      select: (res) => res.data,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Retrieve the E-Invoice details.
 * @param {number} invoiceId - Invoice id.
 */
export function useCategorySales(props, requestProps) {
  return useRequestQuery(
    [t.CATEGORY_SALES],
    { method: 'get', url: `sales/invoices/dashboard/categorySales`, ...requestProps },
    {
      select: (res) => res.data,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Marks the sale invoice as delivered.
 */
export function useDeliverInvoice(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    (invoiceId) => apiRequest.post(`sales/invoices/${invoiceId}/deliver`),
    {
      onSuccess: (res, invoiceId) => {
        // Invalidate specific invoice.
        queryClient.invalidateQueries([t.SALE_INVOICE, invoiceId]);

        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Retrieve the sale invoice details.
 * @param {number} invoiceId - Invoice id.
 */
export function useInvoice(invoiceId, props, requestProps) {
  return useRequestQuery(
    [t.SALE_INVOICE, invoiceId],
    { method: 'get', url: `sales/invoices/${invoiceId}`, ...requestProps },
    {
      select: (res) => res.data.sale_invoice,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Retrieve the E-Invoice details.
 * @param {number} invoiceId - Invoice id.
 */
export function useEInvoice(invoiceId, props, requestProps) {
  return useRequestQuery(
    [t.E_SALE_INVOIVE, invoiceId],
    { method: 'get', url: `sales/invoices/${invoiceId}/e_invoice`, ...requestProps },
    {
      select: (res) => res.data,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Retrieve the invoice pdf document data.
 */
export function usePdfInvoice(invoiceId) {
  return useRequestPdf({
    url: `sales/invoices/${invoiceId}`,
  });
}

/**
 * Retrieve due invoices of the given customer id.
 * @param {number} customerId - Customer id.
 */
export function useDueInvoices(customerId, props) {
  return useRequestQuery(
    [t.SALE_INVOICES, t.SALE_INVOICES_DUE, customerId],
    {
      method: 'get',
      url: `sales/invoices/payable`,
      params: { customer_id: customerId },
    },
    {
      select: (res) => res.data.sales_invoices,
      defaultData: [],
      ...props,
    },
  );
}

export function useRefreshInvoices() {
  const queryClient = useQueryClient();

  return {
    refresh: () => {
      queryClient.invalidateQueries(t.SALE_INVOICES);
    },
  };
}

export function useCreateBadDebt(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/invoices/${id}/writeoff`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Invalidate
        queryClient.invalidateQueries([t.BAD_DEBT, id]);

        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

export function useCancelBadDebt(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    (id) => apiRequest.post(`sales/invoices/${id}/writeoff/cancel`),
    {
      onSuccess: (res, id) => {
        // Invalidate
        queryClient.invalidateQueries([t.CANCEL_BAD_DEBT, id]);

        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

export function useCreateNotifyInvoiceBySMS(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) =>
      apiRequest.post(`sales/invoices/${id}/notify-by-sms`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Invalidate
        queryClient.invalidateQueries([t.NOTIFY_SALE_INVOICE_BY_SMS, id]);

        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

export function useInvoiceSMSDetail(invoiceId, query, props) {
  return useRequestQuery(
    [t.SALE_INVOICE_SMS_DETAIL, invoiceId, query],
    {
      method: 'get',
      url: `sales/invoices/${invoiceId}/sms-details`,
      params: query,
    },
    {
      select: (res) => res.data.data,
      defaultData: {},
      ...props,
    },
  );
}

export function useInvoicePaymentTransactions(invoiceId, props) {
  return useRequestQuery(
    [t.SALE_INVOICE_PAYMENT_TRANSACTIONS, invoiceId],
    {
      method: 'get',
      url: `sales/invoices/${invoiceId}/payment-transactions`,
    },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}

export function useSendSaleInvoiceMail(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();
 console.log('useSendSaleInvoiceMail - Coming up to here check out this imp ', props);
  return useMutation(
    ([id, values]) => apiRequest.post(`sales/invoices/${id}/mail`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

export function useSaleInvoiceDefaultOptions(invoiceId, props) {
  console.log('useSaleInvoiceDefaultOptions - Coming up to here check out this imp ', invoiceId);
  return useRequestQuery(
    [t.SALE_INVOICE_DEFAULT_OPTIONS, invoiceId],
    {
      method: 'get',
      url: `sales/invoices/${invoiceId}/mail`,
    },
    {
      select: (res) => res.data.data,
      ...props,
    },
  );
}

const toNumberSafe = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeCommissionRow = (commission) => ({
  ...commission,
  _id: String(commission?._id ?? commission?.id ?? ''),
  commissionRate: toNumberSafe(
    commission?.commissionRate ?? commission?.commission_rate,
    0,
  ),
  profitRate: toNumberSafe(commission?.profitRate ?? commission?.profit_rate, 0),
  amount: toNumberSafe(commission?.amount, 0),
  due: toNumberSafe(commission?.due, 0),
  payable: Boolean(commission?.payable),
  status: commission?.status ?? 'unpaid',
  createdAt: commission?.createdAt ?? commission?.created_at ?? null,
  updatedAt: commission?.updatedAt ?? commission?.updated_at ?? null,
});

const normalizePaymentEntry = (entry) => ({
  ...entry,
  _id: String(entry?._id ?? entry?.id ?? ''),
  entityId: String(entry?.entityId ?? entry?.saleInvoiceCommissionId ?? ''),
  entityNumber: entry?.entityNumber ?? entry?.number ?? '',
  amount: toNumberSafe(entry?.amount, 0),
  appliedAmount: toNumberSafe(entry?.appliedAmount ?? entry?.applied_amount, 0),
  due: toNumberSafe(entry?.due, 0),
});

const normalizePaymentRow = (row) => ({
  ...row,
  _id: String(row?._id ?? row?.id ?? ''),
  paymentId: String(row?.paymentId ?? row?.payment_id ?? ''),
  paymentName:
    row?.paymentName ?? row?.payment_name ?? row?.name ?? row?.paymentId ?? '',
  invoicesCount: toNumberSafe(
    row?.invoicesCount ?? row?.invoices_count ?? row?.invoices,
    0,
  ),
  amount: toNumberSafe(row?.amount, 0),
  originalAmount: toNumberSafe(row?.originalAmount ?? row?.original_amount, 0),
  due: toNumberSafe(row?.due, 0),
  paymentDate:
    row?.paymentDate ??
    row?.payment_date ??
    row?.createdAt ??
    row?.created_at ??
    null,
  createdAt: row?.createdAt ?? row?.created_at ?? null,
  updatedAt: row?.updatedAt ?? row?.updated_at ?? null,
  salesperson: row?.salesperson
    ? {
        _id: String(row.salesperson?._id ?? row.salesperson?.id ?? ''),
        name: row.salesperson?.name ?? '',
      }
    : null,
  entries: (row?.entries ?? []).map(normalizePaymentEntry),
});

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildPaymentRowsFromPaymentPayload = (payment) => {
  if (!payment) return [];

  const paymentId = String(payment?._id ?? payment?.id ?? '');
  const paymentName = payment?.name ?? '';
  const status = payment?.status ?? 'applied';
  const createdAt = payment?.createdAt ?? payment?.created_at ?? null;
  const updatedAt = payment?.updatedAt ?? payment?.updated_at ?? createdAt;

  const groups = new Map();

  for (const rawEntry of payment?.entries ?? []) {
    const entry = normalizePaymentEntry(rawEntry);
    const salespersonId = String(
      rawEntry?.salesperson?._id ?? rawEntry?.salesperson?.id ?? '',
    );
    const salespersonName = rawEntry?.salesperson?.name ?? '';
    const key = salespersonId || salespersonName || 'unknown';

    if (!groups.has(key)) {
      groups.set(key, {
        salesperson: { _id: salespersonId, name: salespersonName },
        entries: [],
        amount: 0,
        originalAmount: 0,
        due: 0,
      });
    }

    const group = groups.get(key);
    group.entries.push(entry);
    group.amount += toNumberSafe(entry?.appliedAmount, 0);
    group.originalAmount += toNumberSafe(entry?.amount, 0);
    group.due += toNumberSafe(entry?.due, 0);
  }

  return Array.from(groups.entries()).map(([groupKey, group]) =>
    normalizePaymentRow({
      _id: `${paymentId}:${groupKey}`,
      paymentId,
      paymentName,
      salesperson: group.salesperson,
      invoicesCount: group.entries.length,
      amount: group.amount,
      originalAmount: group.originalAmount,
      due: group.due,
      status,
      paymentDate: createdAt,
      createdAt,
      updatedAt,
      entries: group.entries,
    }),
  );
};

const updateMatchingCommissionQueries = (
  queryClient,
  predicate,
  updater,
) => {
  const queryCache = queryClient?.getQueryCache?.();
  const queries = queryCache?.findAll?.() ?? [];

  queries.forEach((query) => {
    const queryKey = query?.queryKey;
    if (!predicate(queryKey)) return;

    queryClient.setQueryData(queryKey, (previous) => updater(previous, queryKey));
  });
};

const updateCommissionListCacheByMutationPayload = (queryClient, payload = {}) => {
  const paymentRows = payload?.payment
    ? buildPaymentRowsFromPaymentPayload(payload.payment)
    : [];
  const affectedPaymentId = String(
    payload?.payment?._id ?? payload?.paymentId ?? payload?.id ?? '',
  );
  const affectedCommissions = [
    ...(payload?.updatedCommissions ?? []),
    ...(payload?.revertedCommissions ?? []),
  ].map(normalizeCommissionRow);

  if (affectedPaymentId) {
    updateMatchingCommissionQueries(
      queryClient,
      (queryKey) =>
        Array.isArray(queryKey) &&
        queryKey[0] === t.COMMISSIONS_LIST &&
        queryKey[1] === 'payments',
      (previous) => {
        if (!previous) return previous;

        const currentRows = (previous?.commissions ?? []).map(normalizePaymentRow);
        const untouchedRows = currentRows.filter(
          (row) => String(row?.paymentId ?? '') !== affectedPaymentId,
        );
        const mergedRows = [...paymentRows, ...untouchedRows].sort(
          (a, b) =>
            getTimeValue(b?.updatedAt ?? b?.createdAt) -
            getTimeValue(a?.updatedAt ?? a?.createdAt),
        );

        return { ...previous, commissions: mergedRows };
      },
    );
  }

  if (affectedCommissions.length) {
    updateMatchingCommissionQueries(
      queryClient,
      (queryKey) =>
        Array.isArray(queryKey) &&
        queryKey[0] === t.COMMISSIONS_LIST &&
        queryKey[1] === 'payable',
      (previous) => {
        if (!previous) return previous;

        const byId = new Map<string, any>();
        (previous?.commissions ?? [])
          .map(normalizeCommissionRow)
          .forEach((row) => {
            if (row?._id) byId.set(String(row._id), row);
          });

        for (const updatedCommission of affectedCommissions) {
          const status = String(updatedCommission?.status ?? '').toLowerCase();
          const shouldExist = Boolean(updatedCommission?.payable) && status !== 'paid';

          if (shouldExist) {
            byId.set(String(updatedCommission._id), updatedCommission);
          } else {
            byId.delete(String(updatedCommission._id));
          }
        }

        const nextRows = Array.from(byId.values()).sort(
          (a, b) =>
            getTimeValue(b?.payableDate ?? b?.updatedAt ?? b?.createdAt) -
            getTimeValue(a?.payableDate ?? a?.updatedAt ?? a?.createdAt),
        );

        return { ...previous, commissions: nextRows };
      },
    );
  }
};

export function useCommissionsList(listType = 'accrued', query = {}, props: any = {}) {
  return useRequestQuery(
    [t.COMMISSIONS_LIST, listType, query],
    {
      method: 'get',
      url: `sales/commissions`,
      params: { listType, ...query },
    },
    {
      select: (res) => {
        const commissions = (res?.data?.commissions ?? []).map((commission) =>
          listType === 'payments'
            ? normalizePaymentRow(commission)
            : normalizeCommissionRow(commission),
        );

        return { ...res.data, commissions };
      },
      ...props,
    },
  );
}

export function useCreateCommissionPayment(props?) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();
  return useMutation(
    (dto) => apiRequest.post('sales/commissions/payments', dto),
    {
      onSuccess: (res, variables, context) => {
        queryClient.invalidateQueries(t.COMMISSIONS_LIST);
        props?.onSuccess?.(res, variables, context);
      },
      ...props,
    },
  );
}

export function useCommissionPayment(paymentId: string | number | null, props?) {
  const apiRequest = useApiRequest();

  return useRequestQuery(
    [t.COMMISSION_PAYMENT, paymentId],
    {
      method: 'get',
      url: `sales/commissions/payments/${paymentId}`,
    },
    {
      enabled: !!paymentId,
      select: (res) => {
        const row = res?.data ?? res;
        return row ? normalizePaymentRow(row) : null;
      },
      staleTime: 0, // always re-fetch fresh on open
      ...props,
    },
  );
}

export function useEditCommissionPayment(props?) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([paymentId, values]) =>
      apiRequest.put(`sales/commissions/payments/${paymentId}`, values),
    {
      ...props,
      onSuccess: (res, variables, context) => {
        const payload = res?.data ?? res;
        const paymentId = Array.isArray(variables) ? variables[0] : null;
        updateCommissionListCacheByMutationPayload(queryClient, payload);

        if (paymentId) {
          queryClient.invalidateQueries([t.COMMISSION_PAYMENT, paymentId]);
          if (payload?.payment) {
            queryClient.setQueryData([t.COMMISSION_PAYMENT, paymentId], { data: payload.payment });
          }
        }
        queryClient.invalidateQueries([t.COMMISSIONS_LIST, 'payable'], {
          refetchInactive: true,
        });
        queryClient.invalidateQueries([t.COMMISSIONS_LIST, 'payments'], {
          refetchInactive: true,
        });
        queryClient.invalidateQueries([t.COMMISSIONS_LIST, 'accrued'], {
          refetchInactive: true,
        });
        props?.onSuccess?.(res, variables, context);
      },
    },
  );
}

export function useDeleteCommissionPayment(props?) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    (paymentId) => apiRequest.delete(`sales/commissions/payments/${paymentId}`),
    {
      ...props,
      onSuccess: (res, variables, context) => {
        queryClient.invalidateQueries(t.COMMISSIONS_LIST);
        props?.onSuccess?.(res, variables, context);
      },
    },
  );
}